import React, { useEffect, useRef } from 'react';

export const WaveBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width: number, height: number;
    let frameId: number;

    const resize = () => {
      // Force DPR to 1 to drastically improve performance on complex line rendering
      const dpr = 1;
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    };

    window.addEventListener('resize', resize);
    resize();

    let time = 0;
    const numLines = 65; // Denser ribbon effect

    // Dynamically calculate smooth gradient across the ribbon
    const getStrokeColor = (index: number, total: number) => {
      const ratio = index / total;
      let r, g, b;
      if (ratio < 0.5) {
        const t = ratio * 2;
        // Brand Accent (240, 83, 15) -> Brand Warn (217, 148, 24)
        r = 240 + (217 - 240) * t;
        g = 83 + (148 - 83) * t;
        b = 15 + (24 - 15) * t;
      } else {
        const t = (ratio - 0.5) * 2;
        // Brand Warn (217, 148, 24) -> Soft Blue (100, 150, 220)
        r = 217 + (100 - 217) * t;
        g = 148 + (150 - 148) * t;
        b = 24 + (220 - 24) * t;
      }
      return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, 0.35)`;
    };

    const render = () => {
      time += 0.004; // Animation speed
      ctx.clearRect(0, 0, width, height);

      const centerY = height / 2;

      for (let i = 0; i < numLines; i++) {
        ctx.beginPath();
        const lineOffset = i * 1.2; // Tighter vertical spread for depth
        const phaseShift = i * 0.04; // Tighter phase shift 

        for (let x = 0; x <= width; x += 15) {
          // Optimized math calculations
          const y1 = Math.sin(x * 0.003 + time * 3 + phaseShift) * (140 + i * 1.5);
          const y2 = Math.cos(x * 0.0015 - time * 2 + phaseShift) * 90;

          const y = centerY + y1 + y2 + lineOffset - (numLines * 1.2 / 2);

          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }

        ctx.strokeStyle = getStrokeColor(i, numLines);
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      frameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(frameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      id="hero-canvas"
      className="pointer-events-none absolute inset-0 z-0 opacity-90"
    />
  );
};
